// src/Component/ScrollRevealStats.tsx
import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { Activity, TrendingUp, Zap, Globe2, ShieldCheck, Bitcoin } from 'lucide-react';
import { Link } from 'react-router-dom';

// --- DATA ARRAY FOR EASY EDITING ---
const cardsInfo = [
  {
    title: "Nifty Options", subtitle: "High Liquidity Trades", icon: <Activity className="text-white w-4 h-4 md:w-5 md:h-5" />,
    gradient: "from-emerald-500 to-emerald-700", border: "border-emerald-400", shadow: "shadow-emerald-300/40", textCol: "text-emerald-100",
    stat1: "Win Rate", val1: "82%", stat2: "Avg ROI", val2: "12-15%", stat3: "Strategy:", val3: "Momentum",
    targetX: -340, targetY: -190, mobX: -85, mobY: -220, zIndex: 50, delay: 0 
  },
  {
    title: "BankNifty Scalp", subtitle: "Ultra-fast execution", icon: <Zap className="text-white fill-white/20 w-4 h-4 md:w-5 md:h-5" />,
    gradient: "from-amber-500 to-orange-600", border: "border-amber-400", shadow: "shadow-amber-300/40", textCol: "text-amber-100",
    stat1: "Accuracy", val1: "88%", stat2: "Trades/Day", val2: "15-20", stat3: "Min Cap:", val3: "₹50k",
    targetX: -340, targetY: 230, mobX: -85, mobY: 220, zIndex: 40, delay: 0.3 
  },
  {
    title: "Options Selling", subtitle: "Theta Decay Setup", icon: <ShieldCheck className="text-white w-4 h-4 md:w-5 md:h-5" />,
    gradient: "from-pink-500 to-rose-700", border: "border-pink-400", shadow: "shadow-pink-300/40", textCol: "text-pink-100",
    stat1: "Win Rate", val1: "92%", stat2: "Drawdown", val2: "< 5%", stat3: "Capital:", val3: "₹2L",
    targetX: -620, targetY: 20, mobX: -95, mobY: 0, zIndex: 45, delay: 0.6,
    hideOnMobile: true 
  },
  {
    title: "Swing Trading", subtitle: "Positional Alpha setup", icon: <TrendingUp className="text-white w-4 h-4 md:w-5 md:h-5" />,
    gradient: "from-blue-500 to-blue-700", border: "border-blue-400", shadow: "shadow-blue-300/40", textCol: "text-blue-100",
    stat1: "Holding", val1: "2-5 Days", stat2: "Avg ROI", val2: "5-8%", stat3: "Risk:", val3: "Low",
    targetX: 340, targetY: -190, mobX: 85, mobY: -220, zIndex: 30, delay: 0.9 
  },
  {
    title: "Commodity Bots", subtitle: "MCX Auto-trading", icon: <Globe2 className="text-white w-4 h-4 md:w-5 md:h-5" />,
    gradient: "from-purple-500 to-purple-700", border: "border-purple-400", shadow: "shadow-purple-300/40", textCol: "text-purple-100",
    stat1: "Win Rate", val1: "78%", stat2: "Markets", val2: "Gold, Oil", stat3: "Margin:", val3: "₹1.5L",
    targetX: 340, targetY: 230, mobX: 85, mobY: 220, zIndex: 20, delay: 1.2 
  },
  {
    title: "Crypto Algos", subtitle: "24/7 Auto Trading", icon: <Bitcoin className="text-white w-4 h-4 md:w-5 md:h-5" />,
    gradient: "from-indigo-500 to-blue-800", border: "border-indigo-400", shadow: "shadow-indigo-300/40", textCol: "text-indigo-100",
    stat1: "Active", val1: "24/7", stat2: "Avg ROI", val2: "8-12%", stat3: "Pairs:", val3: "BTC, ETH",
    targetX: 620, targetY: 20, mobX: 95, mobY: 0, zIndex: 25, delay: 1.5,
    hideOnMobile: true 
  }
];

const ScrollRevealStats: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const textOpacity = useTransform(scrollYProgress, [0.2, 0.5], [0, 1]);
  const textScale = useTransform(scrollYProgress, [0.2, 0.5], [0.8, 1]);
  const progress = useTransform(scrollYProgress, [0.2, 0.7], [0, 1]);

  return (
    <section ref={containerRef} className="relative w-full h-[100svh] md:h-[300vh] bg-slate-50 font-[Urbanist]">
      
      <div className="sticky top-0 w-full h-[100svh] md:h-screen flex items-center justify-center overflow-hidden">
        
        {/* Clean Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[800px] h-[300px] md:h-[800px] bg-blue-400/20 md:bg-blue-400/10 rounded-full blur-[80px] md:blur-[120px] pointer-events-none z-0" />

        {/* CENTER MAIN TEXT */}
        <motion.div 
          style={{ 
            opacity: isMobile ? 1 : textOpacity, 
            scale: isMobile ? 1 : textScale 
          }}
          className="absolute z-10 text-center w-full px-4 max-w-[300px] sm:max-w-2xl md:max-w-4xl flex flex-col items-center pointer-events-auto"
        >
          <span className="inline-block py-1.5 px-4 md:px-5 rounded-full bg-blue-100 text-blue-700 text-[9px] md:text-sm font-bold tracking-wide border border-blue-200 uppercase mb-4 shadow-sm">
            The Ultimate Setup
          </span>
          
          <h2 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-tight text-slate-900 mb-3 md:mb-5">
            Join <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500">10,000+</span> <br className="hidden md:block" />
            <span className="md:hidden"> Traders </span>
            <span className="hidden md:inline"> Traders Daily</span>
          </h2>

          <p className="text-slate-600 text-[11px] md:text-lg font-semibold max-w-[260px] md:max-w-2xl text-center leading-relaxed mb-6 md:mb-8">
            Experience powerful algorithmic execution, strict risk-management, and high ROI strategies. Elevate your portfolio in the Indian stock market today.
          </p>

          <Link to="/demat/register" className="inline-block">
  <motion.button 
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    className="px-6 py-3 md:px-8 md:py-4 bg-slate-900 hover:bg-blue-600 text-white rounded-full font-bold text-xs md:text-lg shadow-[0_10px_30px_rgba(0,0,0,0.15)] hover:shadow-[0_10px_30px_rgba(37,99,235,0.3)] transition-colors duration-300"
  >
    Start Trading Setup →
  </motion.button>
</Link>
        </motion.div>

        {/* CARDS */}
        {cardsInfo.map((card, idx) => (
          <AnimatedCard key={idx} progress={progress} card={card} isMobile={isMobile} />
        ))}
      </div>

    </section>
  );
};

const AnimatedCard = ({ progress, card, isMobile }: { progress: MotionValue<number>, card: any, isMobile: boolean }) => {
  // Hooks desktop ke liye humesha run honge
  const desktopX = useTransform(progress, p => p * card.targetX);
  const desktopY = useTransform(progress, p => p * card.targetY);

  return (
    <motion.div 
      style={{ 
        x: isMobile ? card.mobX : desktopX, 
        y: isMobile ? card.mobY : desktopY, 
        zIndex: card.zIndex 
      }} 
      className={`absolute origin-center pointer-events-none md:pointer-events-auto ${card.hideOnMobile ? 'hidden md:block' : ''}`}
    >
      <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: card.delay }}>
        <CardDesign {...card} />
      </motion.div>
    </motion.div>
  );
};

const CardDesign = ({ title, subtitle, icon, gradient, border, shadow, textCol, stat1, val1, stat2, val2, stat3, val3 }: any) => (
  <div className={`w-[130px] sm:w-[160px] md:w-[240px] bg-gradient-to-br ${gradient} border ${border} rounded-[12px] md:rounded-[20px] p-2 md:p-4 shadow-xl md:shadow-2xl ${shadow} flex flex-col gap-1.5 md:gap-3 backdrop-blur-sm`}>
    
    <div className="flex items-center gap-1.5 md:gap-2.5">
      <div className="w-7 h-7 md:w-10 md:h-10 shrink-0 rounded-[8px] md:rounded-xl bg-white/20 flex items-center justify-center border border-white/30 shadow-inner">
        {icon}
      </div>
      <div className="overflow-hidden">
        <h3 className="text-[10px] md:text-base font-extrabold text-white leading-tight truncate">{title}</h3>
        <p className={`text-[7px] md:text-[10px] font-medium ${textCol} mt-[1px] truncate`}>{subtitle}</p>
      </div>
    </div>
    
    <div className="w-full h-[1px] bg-white/20 my-0 md:my-0.5" />
    
    <div className="grid grid-cols-2 gap-1 md:gap-2">
      <div className="bg-black/10 rounded-[6px] md:rounded-lg p-1 md:p-2 border border-white/10">
        <p className={`text-[6px] md:text-[8px] ${textCol} font-bold uppercase tracking-wider mb-[2px]`}>{stat1}</p>
        <p className="text-[9px] md:text-sm font-black text-white leading-none">{val1}</p>
      </div>
      <div className="bg-black/10 rounded-[6px] md:rounded-lg p-1 md:p-2 border border-white/10">
        <p className={`text-[6px] md:text-[8px] ${textCol} font-bold uppercase tracking-wider mb-[2px]`}>{stat2}</p>
        <p className="text-[9px] md:text-sm font-black text-white leading-none">{val2}</p>
      </div>
      <div className="bg-black/10 rounded-[6px] md:rounded-lg p-1 md:p-2 border border-white/10 col-span-2 flex justify-between items-center">
        <span className={`text-[7px] md:text-[10px] ${textCol} font-bold`}>{stat3}</span>
        <span className="text-[8px] md:text-[11px] font-bold text-white bg-white/20 px-1.5 md:px-2 py-[2px] md:py-0.5 rounded-[4px] md:rounded-md">{val3}</span>
      </div>
    </div>
  </div>
);

export default ScrollRevealStats;